import type * as React from "react";
import { cn } from "../../lib";
import { sqBadgeGradientClass } from "../../lib/sq-color-visual-classes";

/**
 * Anchor positions on a rectangular host, plus **`center`**: flow layout, horizontally
 * centered in the parent (horizontal ribbon only; default notch **`both`**).
 */
export type FlagBannerPosition =
	| "top-left"
	| "top"
	| "top-right"
	| "right"
	| "bottom-right"
	| "bottom"
	| "bottom-left"
	| "left"
	| "center";

/** Corner ribbon vs flat banner strip (triangle cut-out). */
export type FlagBannerVariant = "diagonal" | "horizontal";

/** Which short edge of the horizontal strip is notched toward the card interior. */
export type FlagBannerHorizontalNotch = "right" | "left" | "both";

const CLIP_HORIZONTAL_NOTCH_RIGHT =
	"polygon(0% 0%,100% 0%,calc(100% - 10px) 50%,100% 100%,0% 100%)";
const CLIP_HORIZONTAL_NOTCH_LEFT =
	"polygon(100% 0%,100% 100%,0% 100%,10px 50%,0% 0%)";
const CLIP_HORIZONTAL_NOTCH_BOTH =
	"polygon(0 0,100% 0,calc(100% - 10px) 50%,100% 100%,0 100%,10px 50%)";
const CLIP_HORIZONTAL_NOTCH_BOTH_BOTTOM =
	"polygon(0% 100%,100% 100%,calc(100% - 10px) 50%,100% 0,0% 0%,10px 50%)";

/** Narrower chevron cutouts for compact ribbons (menu coming-soon, etc.). */
const CLIP_HORIZONTAL_NOTCH_RIGHT_COMPACT =
	"polygon(0% 0%,100% 0%,calc(100% - 7px) 50%,100% 100%,0% 100%)";
const CLIP_HORIZONTAL_NOTCH_LEFT_COMPACT =
	"polygon(100% 0%,100% 100%,0% 100%,7px 50%,0% 0%)";
const CLIP_HORIZONTAL_NOTCH_BOTH_COMPACT =
	"polygon(0 0,100% 0,calc(100% - 7px) 50%,100% 100%,0 100%,7px 50%)";
const CLIP_HORIZONTAL_NOTCH_BOTH_BOTTOM_COMPACT =
	"polygon(0% 100%,100% 100%,calc(100% - 7px) 50%,100% 0,0% 0%,7px 50%)";

const EDGE_POSITIONS: ReadonlySet<FlagBannerPosition> = new Set([
	"top",
	"bottom",
	"left",
	"right",
]);

const CORNER_POSITIONS: ReadonlySet<FlagBannerPosition> = new Set([
	"top-left",
	"top-right",
	"bottom-left",
	"bottom-right",
]);

const SHARED_TEXT = "font-black uppercase text-white whitespace-nowrap";

/** Follows clip-path outline (unlike box-shadow on a rectangular wrapper). */
const FLAG_BANNER_DEFAULT_STROKE_FILTER =
	"drop-shadow(0 0 0 1px rgba(255,255,255,0.2))";
const FLAG_BANNER_DEFAULT_SHADOW_FILTER =
	"drop-shadow(0 1px 2px rgb(0 0 0 / 0.05))";

function horizontalRibbonFilter(
	showBorderStroke: boolean,
	showShadow: boolean,
	borderStrokeFilter?: string,
): string | undefined {
	const parts: string[] = [];
	if (showBorderStroke) {
		parts.push(borderStrokeFilter ?? FLAG_BANNER_DEFAULT_STROKE_FILTER);
	}
	if (showShadow) {
		parts.push(FLAG_BANNER_DEFAULT_SHADOW_FILTER);
	}
	return parts.length > 0 ? parts.join(" ") : undefined;
}

const FLAG_BANNER_SIZE_CLASS = {
	default: "text-[10px] tracking-[0.14em]",
	compact: "text-[8px] tracking-[0.1em]",
} as const;

const FLAG_BANNER_HEIGHT_CLASS = {
	default: "h-6 min-h-6",
	compact: "h-4 min-h-4",
} as const;

const FLAG_BANNER_MIN_WIDTH_CLASS = {
	default: "min-w-[5.25rem]",
	compact: "min-w-[4.5rem]",
} as const;

export type FlagBannerSize = keyof typeof FLAG_BANNER_SIZE_CLASS;

/**
 * Shared ribbon backgrounds for entity tiles, magic / bento cards, and marketing accents.
 * `gold` matches `rankGold` (podium #1 / primary gold sash).
 */
export type FlagBannerSurfacePreset =
	| "pick"
	| "new"
	| "trend"
	| "top"
	| "muted"
	| "rankGold"
	| "rankSilver"
	| "rankBronze"
	| "rankOther"
	| "gold";

const FLAG_BANNER_SURFACE_CLASS: Record<
	Exclude<FlagBannerSurfacePreset, "gold">,
	string
> = {
	pick: sqBadgeGradientClass("purple"),
	new: sqBadgeGradientClass("orange"),
	trend: sqBadgeGradientClass("red"),
	/** EXP bar greens. */
	top: sqBadgeGradientClass("green"),
	muted: sqBadgeGradientClass("grey"),
	rankGold: sqBadgeGradientClass("gold"),
	rankSilver: sqBadgeGradientClass("silver"),
	rankBronze: sqBadgeGradientClass("bronze"),
	rankOther: sqBadgeGradientClass("grey"),
};

export function flagBannerSurfaceClassNameForPreset(
	preset: FlagBannerSurfacePreset,
): string {
	if (preset === "gold") {
		return FLAG_BANNER_SURFACE_CLASS.rankGold;
	}
	return FLAG_BANNER_SURFACE_CLASS[preset];
}

/** Rank ribbons for podium (#1 gold, #2 silver, #3 bronze, else dark). */
export function flagBannerSurfaceClassNameForRank(rank: number): string {
	const n = Math.floor(Number(rank));
	if (!Number.isFinite(n)) {
		return FLAG_BANNER_SURFACE_CLASS.rankOther;
	}
	const clamped = Math.max(0, Math.min(9999, n));
	if (clamped === 1) {
		return FLAG_BANNER_SURFACE_CLASS.rankGold;
	}
	if (clamped === 2) {
		return FLAG_BANNER_SURFACE_CLASS.rankSilver;
	}
	if (clamped === 3) {
		return FLAG_BANNER_SURFACE_CLASS.rankBronze;
	}
	return FLAG_BANNER_SURFACE_CLASS.rankOther;
}

function outerPositionClass(position: FlagBannerPosition): string {
	switch (position) {
		case "top-left":
			return "left-0 top-0";
		case "top":
			return "left-1/2 top-0 -translate-x-1/2";
		case "top-right":
			return "right-0 top-0";
		case "right":
			return "right-0 top-1/2 -translate-y-1/2";
		case "bottom-right":
			return "right-0 bottom-0";
		case "bottom":
			return "left-1/2 bottom-0 -translate-x-1/2";
		case "bottom-left":
			return "left-0 bottom-0";
		case "left":
			return "left-0 top-1/2 -translate-y-1/2";
		case "center":
			return "";
		default:
			return "";
	}
}

function inferHorizontalNotch(
	position: FlagBannerPosition,
): FlagBannerHorizontalNotch {
	switch (position) {
		case "top":
			return "both";
		case "bottom":
			return "both";
		case "top-left":
		case "bottom-left":
			return "right";
		case "top-right":
		case "bottom-right":
			return "left";
		case "left":
			return "right";
		case "right":
			return "left";
		case "center":
			return "both";
		default:
			return "right";
	}
}

function clipPathForNotch(
	notch: FlagBannerHorizontalNotch,
	position: FlagBannerPosition,
	size: FlagBannerSize = "default",
): string | undefined {
	const compact = size === "compact";
	if (notch === "both") {
		if (position === "bottom") {
			return compact
				? CLIP_HORIZONTAL_NOTCH_BOTH_BOTTOM_COMPACT
				: CLIP_HORIZONTAL_NOTCH_BOTH_BOTTOM;
		}
		return compact
			? CLIP_HORIZONTAL_NOTCH_BOTH_COMPACT
			: CLIP_HORIZONTAL_NOTCH_BOTH;
	}
	if (notch === "left") {
		return compact
			? CLIP_HORIZONTAL_NOTCH_LEFT_COMPACT
			: CLIP_HORIZONTAL_NOTCH_LEFT;
	}
	return compact
		? CLIP_HORIZONTAL_NOTCH_RIGHT_COMPACT
		: CLIP_HORIZONTAL_NOTCH_RIGHT;
}

function cornerHorizontalInsetStyle(
	position: FlagBannerPosition,
	cornerInsetPx: number | undefined,
): React.CSSProperties {
	if (cornerInsetPx == null) {
		return {};
	}
	switch (position) {
		case "top-left":
			return { top: cornerInsetPx, left: cornerInsetPx };
		case "top-right":
			return { top: cornerInsetPx, right: cornerInsetPx };
		case "bottom-left":
			return { bottom: cornerInsetPx, left: cornerInsetPx };
		case "bottom-right":
			return { bottom: cornerInsetPx, right: cornerInsetPx };
		default:
			return {};
	}
}

function diagonalRotationDeg(position: FlagBannerPosition): number {
	switch (position) {
		case "top-left":
			return -45;
		case "top-right":
			return 45;
		case "bottom-right":
			return -45;
		case "bottom-left":
			return 45;
		default:
			return -45;
	}
}

/** Minimum diagonal strip width (px) before geometric chord drives length. */
const DIAGONAL_CORNER_STRIP_MIN_WIDTH_PX = 178;
const DIAGONAL_CORNER_STRIP_SLACK_PX = 6;

/**
 * Corner sash: origin is the host **outer** corner; `d` is inset along **both** adjacent edges.
 * Strip midpoint `(d/2, d/2)` (bisector). Length `max(178, d√2 + slack)`. Parent should clip with
 * `overflow-hidden` + matching `rounded-*` when needed.
 */
function diagonalCornerStripLengthPx(dInsetPx: number): number {
	const geometricChord =
		dInsetPx * Math.SQRT2 + DIAGONAL_CORNER_STRIP_SLACK_PX;
	return Math.max(DIAGONAL_CORNER_STRIP_MIN_WIDTH_PX, geometricChord);
}

function diagonalSymmetricRibbonCenterStyle(
	position: FlagBannerPosition,
	dInsetPx: number,
): React.CSSProperties {
	const c = dInsetPx / 2;
	switch (position) {
		case "top-left":
			return { left: c, top: c };
		case "top-right":
			return { right: c, top: c };
		case "bottom-left":
			return { left: c, bottom: c };
		case "bottom-right":
			return { right: c, bottom: c };
		default:
			return {};
	}
}

function diagonalSymmetricRibbonTransformForCorner(
	position: FlagBannerPosition,
	rotationDeg: number,
): string {
	switch (position) {
		case "top-left":
			return `translate(-50%, -50%) rotate(${rotationDeg}deg)`;
		case "top-right":
			return `translate(50%, -50%) rotate(${rotationDeg}deg)`;
		case "bottom-left":
			return `translate(-50%, 50%) rotate(${rotationDeg}deg)`;
		case "bottom-right":
			return `translate(50%, 50%) rotate(${rotationDeg}deg)`;
		default:
			return `translate(-50%, -50%) rotate(${rotationDeg}deg)`;
	}
}

export interface FlagBannerProps {
	children: React.ReactNode;
	position: FlagBannerPosition;
	/**
	 * `diagonal` only applies on **corner** positions (default when omitted). Edge
	 * positions always use horizontal geometry; pass `variant="horizontal"` on corners
	 * for list / magic-style ribbons.
	 */
	variant?: FlagBannerVariant;
	/**
	 * Extra classes on the horizontal ribbon root (merged **after** notch-balanced
	 * padding defaults). Pass `px-*` / `pl-*` / `pr-*` here to intentionally override
	 * spacing when needed (label may no longer align with the clip-path apex).
	 */
	className?: string;
	/**
	 * Central gradient presets (entity tiles, magic cards, podium ranks). Merged before
	 * `surfaceClassName` so callers can still override specifics.
	 */
	surfacePreset?: FlagBannerSurfacePreset;
	/** Background / gradient utilities applied to the ribbon surface. */
	surfaceClassName?: string;
	/**
	 * Inline surface fill when CSS utilities cannot express the gradient
	 * (e.g. mythic rarity rainbow from `rarityTierBorderBackground`).
	 */
	surfaceStyle?: React.CSSProperties;
	/** Stacking context, e.g. `z-[5]` (default). */
	zIndexClassName?: string;
	/**
	 * Distance (px) from the host corner along both axes for **horizontal** flags on
	 * **corner** positions — passed as inline `top` / `left` / `right` / `bottom` so the
	 * banner clears a rounded parent (Tailwind cannot express dynamic spacing).
	 */
	cornerInsetPx?: number;
	/**
	 * For `position === "top"` or `"bottom"`: vertical offset (px) so the host edge can
	 * bisect the ribbon (negative pulls past the edge, e.g. fan cards use `-12`).
	 */
	edgeOverflowPx?: number;
	/** Overrides auto-selected notch for horizontal geometry. */
	horizontalNotch?: FlagBannerHorizontalNotch;
	/**
	 * Extra inline styles for the horizontal ribbon (merged last), e.g. entity list tiles
	 * use `{ top: 30, transform: "translateY(-50%)" }` to align with the badges row.
	 */
	horizontalExtraStyle?: React.CSSProperties;
	/**
	 * When false, render the ribbon in normal flow (no absolute edge/corner anchors).
	 * Use when a parent already owns absolute placement (e.g. menu coming-soon overlay).
	 */
	positioned?: boolean;
	/**
	 * Diagonal corners (default variant): inset `d` (px) from the outer corner along **both**
	 * adjacent edges; midpoint `(d/2, d/2)`. Length `max(178, d√2 + slack)`. Clip via parent when
	 * the sash should not extend past the card (`overflow-hidden` + matching `rounded-*`).
	 */
	diagonalInsetFromCornerPx?: number;
	/** Typography / ribbon height; diagonal corners use text sizing only. */
	size?: FlagBannerSize;
	/**
	 * CSS `filter` stroke for horizontal clip-path ribbons (follows notch diagonals).
	 * Defaults to a 1px white/20 outline. Ignored when `borderFilterClassName` is set.
	 */
	borderStrokeFilter?: string;
	/** Tailwind filter utilities for stroke (e.g. dark-mode variants). Overrides `borderStrokeFilter`. */
	borderFilterClassName?: string;
	/** When false, render fill clip only (no notch stroke layer). */
	showBorderStroke?: boolean;
	/** When false, omit the default ribbon drop shadow. */
	showShadow?: boolean;
}

/**
 * Banner-shaped flag / ribbon for cards and tiles: **diagonal** corner sash or
 * **horizontal** strip with triangular cut-out(s). **`center`** is in-flow, full-width flex
 * centering (horizontal ribbon; notch defaults **`both`**).
 *
 * Pass `surfacePreset` and/or `surfaceClassName` (e.g. extra shadows) and `children` (label).
 */
export function FlagBanner({
	children,
	position,
	variant,
	className,
	surfacePreset,
	surfaceClassName,
	surfaceStyle,
	zIndexClassName = "z-[5]",
	cornerInsetPx,
	edgeOverflowPx,
	horizontalNotch,
	horizontalExtraStyle,
	diagonalInsetFromCornerPx = 12,
	size = "default",
	positioned = true,
	borderStrokeFilter,
	borderFilterClassName,
	showBorderStroke = true,
	showShadow = true,
}: FlagBannerProps) {
	const sizeTextClass = FLAG_BANNER_SIZE_CLASS[size];
	const sizeHeightClass = FLAG_BANNER_HEIGHT_CLASS[size];
	const sizeMinWidthClass = FLAG_BANNER_MIN_WIDTH_CLASS[size];
	const resolvedSurfaceClassName = cn(
		surfacePreset != null
			? flagBannerSurfaceClassNameForPreset(surfacePreset)
			: undefined,
		surfaceClassName,
	);

	const isCenter = position === "center";
	const isEdge = EDGE_POSITIONS.has(position);
	const isCorner = CORNER_POSITIONS.has(position);
	/** Edge / center anchors use horizontal ribbons; corners default to diagonal (grid sash). */
	const resolvedVariant: FlagBannerVariant =
		isCenter || isEdge ? "horizontal" : (variant ?? "diagonal");

	if (resolvedVariant === "diagonal" && isCorner) {
		const rotation = diagonalRotationDeg(position);
		const d = diagonalInsetFromCornerPx;
		const stripLengthPx = diagonalCornerStripLengthPx(d);
		return (
			<div
				className={cn(
					"pointer-events-none absolute overflow-visible",
					zIndexClassName,
					outerPositionClass(position),
					className,
				)}
				style={{ width: 0, height: 0 }}
			>
				<div
					className={cn(
						"absolute flex items-center justify-center border border-white/20 px-2 py-1 text-center shadow-sm",
						SHARED_TEXT,
						sizeTextClass,
						resolvedSurfaceClassName,
					)}
					style={{
						width: stripLengthPx,
						minHeight: "1.5rem",
						...diagonalSymmetricRibbonCenterStyle(position, d),
						transform: diagonalSymmetricRibbonTransformForCorner(
							position,
							rotation,
						),
						...surfaceStyle,
					}}
				>
					{children}
				</div>
			</div>
		);
	}

	if (position === "center") {
		const notch = horizontalNotch ?? "both";
		const clipPath = clipPathForNotch(notch, "top", size);
		let horizontalPadding = size === "compact" ? "px-3" : "pl-2 pr-[18px]";
		if (notch === "both") {
			horizontalPadding = size === "compact" ? "px-3" : "px-[18px]";
		} else if (notch === "left") {
			horizontalPadding =
				size === "compact"
					? "px-2.5 pl-3 pr-2.5"
					: "px-2 pl-[18px] pr-2";
		}
		const ribbonFilter = borderFilterClassName
			? undefined
			: horizontalRibbonFilter(
					showBorderStroke,
					showShadow,
					borderStrokeFilter,
				);
		return (
			<div
				className={cn(
					"pointer-events-none flex w-full shrink-0 justify-center",
					zIndexClassName,
				)}
			>
				<span
					className={cn(
						"inline-flex shrink-0 items-center justify-center",
						sizeHeightClass,
						sizeMinWidthClass,
						SHARED_TEXT,
						sizeTextClass,
						resolvedSurfaceClassName,
						horizontalPadding,
						borderFilterClassName,
						className,
					)}
					style={{
						clipPath,
						filter: ribbonFilter,
						...surfaceStyle,
					}}
				>
					{children}
				</span>
			</div>
		);
	}

	const notch = horizontalNotch ?? inferHorizontalNotch(position);
	const clipPath = clipPathForNotch(notch, position, size);
	const cornerStyle = isCorner
		? cornerHorizontalInsetStyle(position, cornerInsetPx)
		: {};

	const edgeOverflowStyle: React.CSSProperties = {};
	if (position === "top") {
		edgeOverflowStyle.top = edgeOverflowPx ?? -12;
	}
	if (position === "bottom") {
		edgeOverflowStyle.bottom = edgeOverflowPx ?? -12;
	}

	let horizontalPadding = size === "compact" ? "px-2.5" : "pl-2 pr-[18px]";
	if (notch === "both") {
		horizontalPadding = size === "compact" ? "px-3" : "px-[18px]";
	} else if (notch === "left") {
		horizontalPadding =
			size === "compact" ? "px-2.5 pl-3 pr-2.5" : "px-2 pl-[18px] pr-2";
	}

	const rotateForVerticalEdge =
		position === "left"
			? "-rotate-90"
			: position === "right"
				? "rotate-90"
				: "";

	const ribbonFilter = borderFilterClassName
		? undefined
		: horizontalRibbonFilter(
				showBorderStroke,
				showShadow,
				borderStrokeFilter,
			);

	return (
		<span
			className={cn(
				"pointer-events-none inline-flex w-max shrink-0 items-center justify-center",
				positioned && "absolute",
				sizeHeightClass,
				sizeMinWidthClass,
				zIndexClassName,
				positioned ? outerPositionClass(position) : null,
				SHARED_TEXT,
				sizeTextClass,
				resolvedSurfaceClassName,
				rotateForVerticalEdge,
				horizontalPadding,
				borderFilterClassName,
				className,
			)}
			style={{
				clipPath,
				filter: ribbonFilter,
				...(positioned ? cornerStyle : {}),
				...(positioned ? edgeOverflowStyle : {}),
				...(positioned ? horizontalExtraStyle : {}),
				...surfaceStyle,
			}}
		>
			{children}
		</span>
	);
}
